import React from 'react';

import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ActionIcon,
  Box,
  Button,
  Grid,
  Group,
  Modal,
  MultiSelect,
  Paper,
  Select,
  Stack,
  TagsInput,
  Text,
  TextInput,
  Title
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import CarePlanEditor from './CarePlanEditor';
import { SYSTEM_CONFIGS } from '../../utils/integration';
import { PostJSON, usePostJSON } from '../../utils/request';
import type { CareManager } from '../caremanager/types';

export interface ClientExtraEntry {
  id: number;
  clientId: number;
  externalSystem: string | null;
  externalId: string | null;
  metadata: unknown;
  createdAt: string;
  updatedAt: string;
}

export interface ClientFormData {
  firstName: string;
  lastName: string;
  phone?: string;
  email?: string;
  dob?: string;
  tags?: string[];
  externalSystem?: string;
  externalId?: string;
}

export interface ClientFormProps {
  initialData?: ClientFormData & { id?: number; carePlan?: string; };
  onSuccess?: () => void;
  onCancel?: () => void;
  mode?: 'add' | 'edit';
}

const ClientForm: React.FC<ClientFormProps> = ({
  initialData,
  onSuccess,
  onCancel,
  mode = 'add'
}) => {
  // Strip +1 prefix from phone number for display
  const getDisplayPhone = (phone?: string): string => {
    if (!phone) return '';
    // Remove +1 prefix if present
    return phone.replace(/^\+1/, '');
  };

  const form = useForm<ClientFormData>({
    initialValues: {
      firstName: initialData?.firstName || '',
      lastName: initialData?.lastName || '',
      phone: getDisplayPhone(initialData?.phone),
      email: initialData?.email || '',
      dob: initialData?.dob || '',
      tags: initialData?.tags || [],
      externalSystem: initialData?.externalSystem || '',
      externalId: initialData?.externalId || ''
    },
    validate: {
      firstName: (value) => (!value ? 'First name is required' : null),
      lastName: (value) => (!value ? 'Last name is required' : null),
      dob: (value) => (!value ? 'Date of birth is required' : null),
      phone: (value) => {
        if (!value) return null; // Phone is optional
        // Remove all non-digit characters for validation
        const digitsOnly = value.replace(/\D/g, '');
        // Check if it's exactly 10 digits
        if (digitsOnly.length === 10) {
          return null; // Valid 10-digit number
        } else {
          return 'Please enter a valid US phone number (10 digits)';
        }
      },
      email: (value) => {
        if (!value) return null; // Email is optional
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return !emailRegex.test(value) ? 'Invalid email format' : null;
      }
    }
  });

  const [carePlanContent, setCarePlanContent] = React.useState(initialData?.carePlan || '');
  const [extras, setExtras] = React.useState<ClientExtraEntry[]>([]);
  const [extrasLoading, setExtrasLoading] = React.useState(false);
  const [removingExtra, setRemovingExtra] = React.useState<ClientExtraEntry | null>(null);
  const [showAddExtraForm, setShowAddExtraForm] = React.useState(false);
  const addExtraForm = useForm<{ externalSystem: string; externalId: string }>({
    initialValues: { externalSystem: '', externalId: '' },
    validate: {
      externalSystem: (v) => (!v ? 'Select a system' : null),
      externalId: (v) => (!v ? 'External ID is required' : null)
    }
  });

  // Care manager assignment
  const { data: cmListData, loading: cmListLoading } = usePostJSON<{
    success: boolean;
    careManagers: CareManager[];
    total: number;
  }>('/api/care_manager/data', { page: 1, limit: 100 }, []);

  const { data: assignedCmData, refetch: refetchAssignedCms } = usePostJSON<{
    success: boolean;
    careManagers: CareManager[];
  }>('/api/care_manager/for_client', { clientId: initialData?.id }, [initialData?.id]);

  const allCareManagers = cmListData?.careManagers || [];
  const assignedCareManagers = assignedCmData?.careManagers || [];
  const assignedCmIds = assignedCareManagers.map(cm => String(cm.id));

  const handleCareManagerChange = async (selectedIds: string[]) => {
    const currentIds = new Set(assignedCmIds);
    const newIds = new Set(selectedIds);

    // Assign newly selected
    for (const id of selectedIds) {
      if (!currentIds.has(id)) {
        try {
          await PostJSON('/api/care_manager/assign', {
            careManagerId: parseInt(id),
            clientId: initialData?.id
          });
        } catch (err) {
          notifications.show({
            title: 'Error',
            message: err instanceof Error ? err.message : 'Failed to assign care manager',
            color: 'red'
          });
        }
      }
    }

    // Unassign removed
    for (const id of assignedCmIds) {
      if (!newIds.has(id)) {
        try {
          await PostJSON('/api/care_manager/unassign', {
            careManagerId: parseInt(id),
            clientId: initialData?.id
          });
        } catch (err) {
          notifications.show({
            title: 'Error',
            message: err instanceof Error ? err.message : 'Failed to unassign care manager',
            color: 'red'
          });
        }
      }
    }

    void refetchAssignedCms();
  };

  const fetchExtras = React.useCallback(async () => {
    if (mode !== 'edit' || !initialData?.id) {
      setExtras([]);
      return;
    }
    setExtrasLoading(true);
    try {
      const res = await PostJSON<{ success: boolean; extras: ClientExtraEntry[] }>(
        '/api/client/extras',
        { clientId: initialData.id }
      );
      if (res.success) setExtras(res.extras);
    } catch {
      setExtras([]);
    } finally {
      setExtrasLoading(false);
    }
  }, [mode, initialData?.id]);

  React.useEffect(() => {
    void fetchExtras();
  }, [fetchExtras]);

  // Format phone number to +1XXXXXXXXXX format
  const formatPhoneNumber = (phone: string): string => {
    if (!phone) return phone;
    // Remove all non-digit characters
    const digitsOnly = phone.replace(/\D/g, '');
    
    if (digitsOnly.length === 10) {
      // 10-digit number: add +1 prefix
      return `+1${digitsOnly}`;
    }
    
    // Return original if not a valid format
    return phone;
  };

  // Update care plan content when initialData changes (for edit mode)
  React.useEffect(() => {
    if (initialData?.carePlan) {
      setCarePlanContent(initialData.carePlan);
    }
  }, [initialData?.carePlan]);

  const handleSubmit = async (values: ClientFormData) => {
    try {
      const submitData = {
        ...values,
        phone: values.phone ? formatPhoneNumber(values.phone) : values.phone
      };

      let isEdit = false;
      if (mode === 'edit' && initialData?.id) {
        isEdit = true;
        // Edit existing client
        await PostJSON('/api/client/edit', {
          id: initialData.id,
          ...submitData
        });
      } else {
        // Add new client with care plan
        await PostJSON('/api/client/create', {
          ...submitData,
          carePlan: carePlanContent
        });
      }
      
      if (!isEdit) {
        form.reset();
        setCarePlanContent('');
      }
      onSuccess?.();
    } catch (error) {
      console.error('Error saving client:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to save client. Please try again.',
        color: 'red'
      });
    }
  };

  const handleAddClient = async () => {
    const result = form.validate();
    if (result.hasErrors) {
      return;
    }
    await handleSubmit(form.values);
  };

  const handleCarePlanSave = async (content: string) => {
    try {
      if (mode === 'edit' && initialData?.id) {
        // Update care plan for existing client
        await PostJSON('/api/client/edit', {
          id: initialData.id,
          carePlan: content
        });
        
        notifications.show({
          title: 'Success',
          message: 'Care plan updated successfully',
          color: 'green'
        });
      } else {
        notifications.show({
          title: 'Success',
          message: 'Care plan saved locally. Save the client to persist changes.',
          color: 'blue'
        });
      }
      setCarePlanContent(content);
    } catch (error) {
      console.error('Error saving care plan:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to save care plan. Please try again.',
        color: 'red'
      });
    }
  };

  const handleAddExtra = async (values: { externalSystem: string; externalId: string }) => {
    if (!initialData?.id) return;
    try {
      await PostJSON('/api/client/add_extra', {
        clientId: initialData.id,
        externalSystem: values.externalSystem,
        externalId: values.externalId
      });
      addExtraForm.reset();
      setShowAddExtraForm(false);
      await fetchExtras();
      notifications.show({
        title: 'Success',
        message: 'External link added',
        color: 'green'
      });
    } catch (err) {
      console.error('Error adding client extra:', err);
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to add external link.',
        color: 'red'
      });
    }
  };

  const handleConfirmRemoveExtra = async () => {
    if (!initialData?.id || !removingExtra?.externalSystem) return;
    const externalSystem = removingExtra.externalSystem;
    setRemovingExtra(null);
    try {
      await PostJSON('/api/client/delete_extra', {
        clientId: initialData.id,
        externalSystem
      });
      await fetchExtras();
      notifications.show({
        title: 'Success',
        message: 'External link removed',
        color: 'green'
      });
    } catch (err) {
      console.error('Error removing client extra:', err);
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to remove external link.',
        color: 'red'
      });
    }
  };

  const systemLabel = (key: string) => SYSTEM_CONFIGS[key]?.name ?? key;

  return (
    <Stack gap="lg">
      <Title order={2}>Client Information</Title>

      {/* Basic Client Information Section */}
      <Paper withBorder p="md">
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <TextInput
              required
              label="First Name"
              placeholder="Enter first name"
              size="xl"
              {...form.getInputProps('firstName')}
            />
            
            <TextInput
              required
              label="Last Name"
              placeholder="Enter last name"
              size="xl"
              {...form.getInputProps('lastName')}
            />

            <DateInput
              required
              label="Date of Birth"
              placeholder="YYYY-MM-DD"
              size="xl"
              valueFormat="YYYY-MM-DD"
              {...form.getInputProps('dob')}
            />
            
            <TextInput
              label="Phone"
              placeholder="Enter phone number"
              size="xl"
              {...form.getInputProps('phone')}
            />
            
            <TextInput
              label="Email"
              placeholder="Enter email address"
              size="xl"
              type="email"
              {...form.getInputProps('email')}
            />
            
            <TagsInput
              label="Tags"
              placeholder="Enter tags"
              size="xl"
              {...form.getInputProps('tags')}
            />
            
            <Select
              clearable
              data={[
                { value: '', label: '-- Not Integrated --' },
                ...Object.values(SYSTEM_CONFIGS).map(config => ({
                  value: config.key,
                  label: config.name
                }))
              ]}
              label="External System"
              placeholder="Select external system"
              size="xl"
              {...form.getInputProps('externalSystem')}
            />
            
            {form.values.externalSystem && (
              <TextInput
                label="External ID"
                placeholder="Enter external ID"
                size="xl"
                {...form.getInputProps('externalId')}
              />
            )}
            
            {
              mode === 'edit' && (
                <Group gap="sm" mt="md">
                  <Button size="xl" type="submit">
                    Update Client Info
                  </Button>
                  {onCancel && (
                    <Button size="xl" variant="outline" onClick={onCancel}>
                      Cancel
                    </Button>
                  )}
                </Group>
              )
            }
          </Stack>
        </form>
      </Paper>

      {mode === 'edit' && initialData?.id && (
        <>
          <div />
          <Title order={2}>Other Connections</Title>
          {extras.length > 0 && (
            <Paper withBorder p="md">
              <Stack gap="md">
                <Stack gap="xs">
                  <Text c="dimmed" fw={500} size="lg">
                    Linked systems
                  </Text>
                  {extras.map((extra) => (
                    <Group key={extra.id} justify="space-between" wrap="nowrap">
                      <Box>
                        <Text fw={500} size="md">
                          {systemLabel(extra.externalSystem ?? '')}
                        </Text>
                        <Text c="dimmed" size="sm">
                          ID: {extra.externalId ?? '—'}
                        </Text>
                      </Box>
                      <ActionIcon
                        aria-label="Remove link"
                        color="red"
                        variant="subtle"
                        onClick={() => setRemovingExtra(extra)}
                      >
                        <FontAwesomeIcon icon={faTrash} size="sm" />
                      </ActionIcon>
                    </Group>
                  ))}
                </Stack>
                {extrasLoading && (
                  <Text c="dimmed" size="sm">
                    Loading…
                  </Text>
                )}
              </Stack>
            </Paper>
          )}

          {!showAddExtraForm ? (
            <div>
              <Button
                size="lg"
                variant="light"
                onClick={() => setShowAddExtraForm(true)}
              >
                Add Another
              </Button>
            </div>
          ) : (
            <Paper withBorder p="md">
              <form onSubmit={addExtraForm.onSubmit((values) => void handleAddExtra(values))}>
                <Stack gap="md">
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 4 }}>
                      <Select
                        data={Object.values(SYSTEM_CONFIGS).map((c) => ({
                          label: c.name,
                          value: c.key
                        }))}
                        label="System"
                        placeholder="System"
                        size="md"
                        {...addExtraForm.getInputProps('externalSystem')}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 8 }}>
                      <TextInput
                        label="External ID"
                        placeholder="External ID"
                        size="md"
                        {...addExtraForm.getInputProps('externalId')}
                      />
                    </Grid.Col>
                  </Grid>
                  <Group gap="sm">
                    <Button size="md" type="submit">
                      Add
                    </Button>
                    <Button
                      size="md"
                      variant="outline"
                      onClick={() => {
                        addExtraForm.reset();
                        setShowAddExtraForm(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </Group>
                </Stack>
              </form>
            </Paper>
          )}

          {/* Remove extra confirmation modal */}
          <Modal
            opened={!!removingExtra}
            size="sm"
            title="Remove external link"
            onClose={() => setRemovingExtra(null)}
          >
            {removingExtra && (
              <Stack gap="md">
                <Text size="md">
                  Remove link to{' '}
                  <Text component="span" fw={500}>
                    {systemLabel(removingExtra.externalSystem ?? '')}
                  </Text>
                  {' '}(ID: {removingExtra.externalId ?? '—'})? This can be re-added later.
                </Text>
                <Group gap="sm" justify="flex-end">
                  <Button size="lg" variant="outline" onClick={() => setRemovingExtra(null)}>
                    Cancel
                  </Button>
                  <Button color="red" size="lg" onClick={() => void handleConfirmRemoveExtra()}>
                    Remove
                  </Button>
                </Group>
              </Stack>
            )}
          </Modal>
        </>
      )}

      {/* Care Manager Assignment */}
      {mode === 'edit' && initialData?.id && (
        <>
          <div />
          <Title order={2}>Care Managers</Title>
          <Paper withBorder p="md">
            <MultiSelect
              clearable
              searchable
              data={allCareManagers.map(cm => ({
                value: String(cm.id),
                label: `${cm.firstName} ${cm.lastName} (${cm.email})`
              }))}
              description="Select care managers to receive periodic check-in digests for this client"
              disabled={cmListLoading}
              label="Assigned Care Managers"
              placeholder={cmListLoading ? 'Loading...' : 'Select care managers'}
              size="lg"
              value={assignedCmIds}
              onChange={(values) => void handleCareManagerChange(values)}
            />
          </Paper>
        </>
      )}

      <div />

      <Title order={2}>Care Plan</Title>

      {/* Care Plan Section */}
      <CarePlanEditor
        initialContent={carePlanContent}
        mode={mode}
        onChange={mode === 'edit' ? undefined : setCarePlanContent}
        onSave={(content) => void handleCarePlanSave(content)}
      />

      {
        mode === 'add' && (
          <Group gap="sm" mt="md">
            <Button size="xl" onClick={() => void handleAddClient()}>
              Add Client
            </Button>
            {onCancel && (
              <Button size="xl" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            )}
          </Group>
        )
      }
    </Stack>
  );
};

export default ClientForm;
